import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    Camera, 
    PlayCircle, 
    Image as ImageIcon, 
    AlertTriangle,
    CheckCircle2,
    Loader2,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Heart,
    MessageCircle,
    Calendar,
    Send,
    Trash2,
    Link2,
    Sparkles,
    ChevronLeft,
    ChevronRight,
    ShieldCheck,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'react-toastify';
import Button from '../common/design-system/Button';
import { Badge } from '../common/design-system/Badge';
import { useAuth } from '../auth/AuthContext';
import InstagramContentPicker from './InstagramContentPicker';
import styles from './CreatorOfferView.module.scss';
import { getCollabMetrics, submitCollabPost, withdrawCollabPost } from '../../services/api';
import { announcePwaInstallOpportunity } from '../../utils/pwa';

const DELIVERABLES = [
    { id: 'post',  title: 'submission.deliverables.post',  icon: ImageIcon,   helper: 'submission.deliverables.post_help' },
    { id: 'story', title: 'submission.deliverables.story', icon: Camera,      helper: 'submission.deliverables.story_help' },
    { id: 'reel',  title: 'submission.deliverables.reel',  icon: PlayCircle,  helper: 'submission.deliverables.reel_help' },
    { id: 'highlight', title: 'submission.deliverables.highlight', icon: Camera, helper: 'submission.deliverables.highlight_help' },
];

const InstagramGlyph = () => (
    <span className={styles['instagram-glyph']} aria-hidden="true">
        <span />
    </span>
);

const formatDate = (ts, locale = 'en-US') => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleDateString(locale, { day: 'numeric', month: 'short' }); }
    catch { return ''; }
};

const formatDeliveryDate = (ts, language = 'es') => {
    if (!ts) return '';
    try {
        const locale = language?.startsWith('pt') ? 'pt-BR' : language?.startsWith('en') ? 'en-US' : 'es-PA';
        return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(ts));
    } catch {
        return '';
    }
};

const formatMetricValue = (n) => {
    if (n === null || n === undefined || n === '') return '—';
    const num = Number(n);
    if (isNaN(num)) return '—';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 10000) return `${Math.round(num / 1000)}K`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return `${num.toLocaleString()}`;
};

const isUsableMetric = value => value !== undefined && value !== null && value !== '';

const isValidInstagramUrl = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return false;
    try {
        const url = new URL(raw.includes('://') ? raw : `https://${raw}`);
        const hostname = (url.hostname || '').toLowerCase().replace(/\.$/, '');
        const isInstagramHost = hostname === 'instagram.com'
            || hostname.endsWith('.instagram.com')
            || hostname === 'instagr.am'
            || hostname.endsWith('.instagr.am');
        return ['http:', 'https:'].includes(url.protocol)
            && isInstagramHost
            && url.pathname.replace(/\//g, '').length > 0;
    } catch {
        return false;
    }
};

const SubmittedCard = ({ item, post, onRefresh, onWithdraw, isRefreshing, isWithdrawing, isInstagramConnected }) => {
    const { t } = useTranslation('creator');
    const [imgError, setImgError] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const Icon = item.icon;
    const insights = post?.insights || {};
    const metricsPending = Boolean(insights.metrics_pending || insights.is_simulated || post?.insights_refresh_state === 'pending');
    const metricsRemoved = insights.metrics_source === 'simulated_metrics_removed';
    const thumbnail = post?.thumbnail_url || post?.media_url;
    const likes = insights.is_simulated ? undefined : insights.likes;
    const comments = insights.is_simulated ? undefined : insights.comments;
    const views = metricsPending ? undefined : (insights.views ?? insights.plays ?? insights.impressions);
    const plays = metricsPending ? undefined : insights.plays;
    const shares = metricsPending ? undefined : insights.shares;
    const engagementRate = metricsPending ? undefined : insights.engagement_rate;
    const emv = post?.emv;
    const isBusinessApproved = Boolean(post?.business_approved);

    return (
        <div className={`${styles['deliverable-card']} ${styles['is-submitted']}`}>
            <div className={styles['card-header']}>
                <div className={styles['type-info']}>
                    <div className={styles['icon-box']}><Icon size={24} /></div>
                     <div className={styles['labels']}>
                         <h4>{t(item.title)} {item.total > 1 ? `(${item.index}/${item.total})` : ''}</h4>
                         <div className={styles['status-row']}>
                             <Badge variant="success" className={styles['submitted-badge']}>{t('submission.status.submitted', 'Entregado')}</Badge>
                         </div>
                     </div>
                </div>
                <div className={styles['card-actions']}>
                    <button
                        type="button"
                        className={styles['card-toggle']}
                        onClick={() => setIsExpanded(prev => !prev)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded
                            ? t('submission.status.collapse', 'Ocultar detalles')
                            : t('submission.status.expand', 'Ver detalles')}
                    >
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onRefresh}
                        disabled={isRefreshing || isWithdrawing}
                        className={styles['refresh-btn']}
                    >
                        <RefreshCw size={14} className={isRefreshing ? styles.spin : undefined} />
                        {isRefreshing ? t('submission.status.refreshing') : t('submission.status.refresh')}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onWithdraw}
                        disabled={isRefreshing || isWithdrawing || isBusinessApproved}
                        title={isBusinessApproved ? t('submission.status.remove_locked', 'Approved deliveries cannot be removed') : undefined}
                        className={styles['refresh-btn']}
                    >
                        {isWithdrawing ? (
                            <Loader2 size={14} className={styles.spin} />
                        ) : (
                            <Trash2 size={14} />
                        )}
                        {isWithdrawing
                            ? t('submission.status.removing')
                            : isBusinessApproved
                                ? t('submission.status.remove_locked', 'Approved')
                                : t('submission.status.remove')}
                    </Button>
                </div>
            </div>

            {isExpanded && <div className={styles['submitted-content']}>
                <div className={styles['visual-block']}>
                    {thumbnail && !imgError ? (
                        <div className={styles['post-preview']}>
                            <img src={thumbnail} alt="Post preview" onError={() => setImgError(true)} />
                        </div>
                    ) : (
                        <div className={styles['post-preview-placeholder']}>
                            <Icon size={32} opacity={0.2} />
                        </div>
                    )}
                    {post?.post_url && (
                        <a
                            href={post.post_url}
                            target="_blank"
                             rel="noreferrer"
                             className={styles['ig-link']}
                         >
                             <InstagramGlyph />
                             {t('submission.status.view_original', 'Ver original')}
                         </a>
                    )}
                </div>

                <div className={styles['metrics-grid']}>
                     <div className={styles['metric-card']}>
                         <div className={styles['metric-label']}><Heart size={12} /> {t('submission.status.likes')}</div>
                         <div className={styles['metric-value']}>{isUsableMetric(likes) ? likes : '-'}</div>
                     </div>
                     <div className={styles['metric-card']}>
                         <div className={styles['metric-label']}><MessageCircle size={12} /> {t('submission.status.comments')}</div>
                         <div className={styles['metric-value']}>{isUsableMetric(comments) ? comments : '-'}</div>
                     </div>
                     <div className={styles['metric-card']}>
                         <div className={styles['metric-label']}><ImageIcon size={12} /> {t('submission.status.views')}</div>
                         <div className={styles['metric-value']}>{isUsableMetric(views) ? views : '-'}</div>
                     </div>
                     {plays > 0 && (
                         <div className={styles['metric-card']}>
                             <div className={styles['metric-label']}><PlayCircle size={12} /> {t('submission.status.plays')}</div>
                             <div className={styles['metric-value']}>{plays}</div>
                         </div>
                     )}
                     {shares > 0 && (
                         <div className={styles['metric-card']}>
                             <div className={styles['metric-label']}><Send size={12} /> {t('submission.status.shares')}</div>
                             <div className={styles['metric-value']}>{shares}</div>
                         </div>
                     )}
                     {engagementRate != null && (
                         <div className={`${styles['metric-card']} ${styles['featured']}`}>
                             <div className={styles['metric-label']}>{t('submission.status.engagement')}</div>
                             <div className={styles['metric-value']}>{engagementRate}%</div>
                         </div>
                     )}
                     {isUsableMetric(emv) && (
                         <div className={`${styles['metric-card']} ${styles['featured']}`}>
                             <div className={styles['metric-label']}><Sparkles size={12} /> {t('performance.emv_label', 'Estimated Media Value')}</div>
                             <div className={styles['metric-value']}>${Number(emv).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                         </div>
                     )}
                </div>
            </div>}
            {isExpanded && <div className={styles['metrics-note-wrap']}>
                <div className={styles['metrics-note']}>
                    <CheckCircle2 size={12} />
                    <span>
                        {post?.insights_refresh_state === 'pending'
                            ? metricsRemoved
                                ? t('submission.metrics_helper.simulated_removed')
                                : t('submission.area.metrics_syncing_help', 'FOTH está sincronizando las métricas de tu contenido. Este proceso se actualiza automáticamente.')
                            : post?.insights_refresh_state === 'frozen'
                                ? t('submission.metrics_helper.historical')
                                : t('submission.metrics_helper.periodic')}
                    </span>
                </div>
            </div>}
            {isExpanded && !isInstagramConnected && (
                <div className={styles['metrics-note-wrap']}>
                    <div className={styles['metrics-note']}>
                        <AlertTriangle size={12} />
                        <span>{t('submission.warnings.connection_lost_metrics')}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Sub-component: CommandDeliverableDetail ---
// Full-width, prominent showcase matching the Business Collab Journey deliverable detail panel
const CommandDeliverableDetail = ({
    item,
    post,
    onRefresh,
    onWithdraw,
    isRefreshing,
    isWithdrawing,
    isInstagramConnected,
    currentUser,
}) => {
    const { t, i18n } = useTranslation('creator');
    const [imgError, setImgError] = useState(false);
    const Icon = item.icon;
    const insights = post?.insights || {};
    const metricsPending = Boolean(insights.metrics_pending || insights.is_simulated || post?.insights_refresh_state === 'pending');
    const metricsRemoved = insights.metrics_source === 'simulated_metrics_removed';
    const thumbnail = post?.thumbnail_url || post?.media_url;
    const likes = insights.is_simulated ? undefined : insights.likes;
    const comments = insights.is_simulated ? undefined : insights.comments;
    const views = metricsPending ? undefined : (insights.views ?? insights.plays ?? insights.impressions ?? insights.video_views);
    const reach = metricsPending ? undefined : (insights.reach ?? insights.impressions);
    const plays = metricsPending ? undefined : insights.plays;
    const shares = metricsPending ? undefined : insights.shares;
    const engagementRate = metricsPending ? undefined : insights.engagement_rate;
    const emv = post?.emv;
    const isBusinessApproved = Boolean(post?.business_approved);

    const postUsername = post?.username
        || post?.insights?.username
        || currentUser?.creator_profile?.instagram_handle
        || currentUser?.creator_profile?.instagram_username
        || currentUser?.username
        || '';
    const displayUsername = postUsername ? (postUsername.startsWith('@') ? postUsername : `@${postUsername}`) : '@creator';
    const contentTypeLabel = t(`picker.types.${item.baseId || item.id}`, item.baseId || item.id);
    const formattedDate = formatDeliveryDate(post?.created_at, i18n.language);

    return (
        <div className={styles['command-detail-showcase']}>
            {/* Hero Panoramic Media Showcase (16:10 aspect ratio matching Business Collab) */}
            <div className={styles['command-preview-wrap']}>
                {thumbnail && !imgError ? (
                    <img
                        className={styles['command-preview-image']}
                        src={thumbnail}
                        alt="Deliverable preview"
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <div className={styles['command-preview-fallback']}>
                        <Icon size={38} className={styles['command-fallback-icon']} />
                        <span>{t(item.title)}</span>
                    </div>
                )}
            </div>

            {/* Metadata Pills Row (@username · Type · Date) */}
            <div className={styles['command-meta-row']}>
                <span className={`${styles['command-pill']} ${styles['is-handle']}`}>
                    {displayUsername}
                </span>
                <span className={styles['command-pill']}>
                    {contentTypeLabel}
                </span>
                {formattedDate && (
                    <span className={styles['command-pill']}>
                        {formattedDate}
                    </span>
                )}
            </div>

            {/* 3-Column Metrics Grid (Views · Reach/Likes · Engagement) */}
            <div className={styles['command-metrics-grid']}>
                <div className={styles['command-metric-box']}>
                    <span>{t('submission.status.views', 'Vistas')}</span>
                    <strong>{isUsableMetric(views) ? formatMetricValue(views) : '—'}</strong>
                </div>
                <div className={styles['command-metric-box']}>
                    <span>
                        {isUsableMetric(reach)
                            ? t('submission.status.reach', 'Alcance')
                            : t('submission.status.likes', 'Likes')}
                    </span>
                    <strong>
                        {isUsableMetric(reach)
                            ? formatMetricValue(reach)
                            : isUsableMetric(likes)
                                ? formatMetricValue(likes)
                                : '—'}
                    </strong>
                </div>
                <div className={styles['command-metric-box']}>
                    <span>{t('submission.status.engagement', 'Tasa de engagement')}</span>
                    <strong>{engagementRate != null ? `${Number(engagementRate).toFixed(1)}%` : '—'}</strong>
                </div>
            </div>

            {/* Secondary Metrics Row (Comments, Plays/Shares, EMV) if available */}
            {(isUsableMetric(comments) || isUsableMetric(shares) || isUsableMetric(plays) || isUsableMetric(emv)) && (
                <div className={styles['command-metrics-grid-secondary']}>
                    {isUsableMetric(comments) && (
                        <div className={styles['command-metric-box']}>
                            <span>{t('submission.status.comments', 'Comentarios')}</span>
                            <strong>{formatMetricValue(comments)}</strong>
                        </div>
                    )}
                    {isUsableMetric(shares) ? (
                        <div className={styles['command-metric-box']}>
                            <span>{t('submission.status.shares', 'Compartidos')}</span>
                            <strong>{formatMetricValue(shares)}</strong>
                        </div>
                    ) : isUsableMetric(plays) ? (
                        <div className={styles['command-metric-box']}>
                            <span>{t('submission.status.plays', 'Reproducciones')}</span>
                            <strong>{formatMetricValue(plays)}</strong>
                        </div>
                    ) : null}
                    {isUsableMetric(emv) && (
                        <div className={`${styles['command-metric-box']} ${styles['is-emv']}`}>
                            <span><Sparkles size={11} /> {t('performance.emv_label', 'EMV Estimado')}</span>
                            <strong>${Number(emv).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </div>
                    )}
                </div>
            )}

            {/* Status Callout Box */}
            {isBusinessApproved ? (
                <div className={`${styles['command-callout']} ${styles['is-approved']}`}>
                    <div className={styles['command-callout-title']}>
                        <ShieldCheck size={16} />
                        <strong>{t('submission.command.approved_title', 'Entrega aprobada por la marca')}</strong>
                    </div>
                    <p>{t('submission.command.approved_body', 'Tu contenido ha sido validado y cumple con los requisitos de la colaboración. El pago o compensación se procesa conforme a las condiciones acordadas.')}</p>
                </div>
            ) : (
                <div className={`${styles['command-callout']} ${styles['is-registered']}`}>
                    <div className={styles['command-callout-title']}>
                        <CheckCircle2 size={16} />
                        <strong>{t('submission.command.registered_title', 'Entrega registrada')}</strong>
                    </div>
                    <p>
                        {post?.insights_refresh_state === 'pending'
                            ? metricsRemoved
                                ? t('submission.metrics_helper.simulated_removed')
                                : t('submission.area.metrics_syncing_help', 'FOTH está sincronizando las métricas de tu contenido. Este proceso se actualiza automáticamente.')
                            : post?.insights_refresh_state === 'frozen'
                                ? t('submission.metrics_helper.historical')
                                : t('submission.command.registered_body', 'Tu entrega ha sido enviada para revisión por la marca. Las métricas de Instagram se actualizan periódicamente.')}
                    </p>
                </div>
            )}

            {/* Disconnected Instagram warning */}
            {!isInstagramConnected && (
                <div className={`${styles['command-callout']} ${styles['is-warning']}`}>
                    <div className={styles['command-callout-title']}>
                        <AlertTriangle size={15} />
                        <strong>{t('submission.warnings.need_connect', 'Conexión requerida')}</strong>
                    </div>
                    <p>{t('submission.warnings.connection_lost_metrics', 'La conexión con Instagram no está activa. Las métricas pueden tardar en actualizarse.')}</p>
                </div>
            )}

            {/* Bottom Actions: View Post, Update Metrics, Withdraw */}
            <div className={styles['command-detail-actions']}>
                {post?.post_url && (
                    <Button
                        variant="primary"
                        href={post.post_url}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <InstagramGlyph />
                        {t('submission.command.view_post', 'Ver publicación')}
                    </Button>
                )}

                <div className={styles['command-secondary-actions']}>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onRefresh}
                        disabled={isRefreshing || isWithdrawing}
                        className={styles['refresh-btn']}
                    >
                        <RefreshCw size={14} className={isRefreshing ? styles.spin : undefined} />
                        {isRefreshing
                            ? t('submission.status.refreshing', 'Actualizando...')
                            : t('submission.command.refresh_metrics', 'Actualizar métricas')}
                    </Button>

                    {!isBusinessApproved && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onWithdraw}
                            disabled={isRefreshing || isWithdrawing}
                            className={styles['refresh-btn']}
                            title={t('submission.command.withdraw_delivery', 'Retirar entrega')}
                        >
                            {isWithdrawing ? (
                                <Loader2 size={14} className={styles.spin} />
                            ) : (
                                <Trash2 size={14} />
                            )}
                            {isWithdrawing
                                ? t('submission.status.removing', 'Retirando...')
                                : t('submission.command.withdraw_delivery', 'Retirar entrega')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Sub-component: PendingCard ---
// Shown when a post_type has NOT been delivered yet
const PendingCard = ({ item, offerId, isInstagramConnected, onInstagramConnect, onSubmitted, commandCenter = false }) => {
    const { t, i18n } = useTranslation('creator');
    const { currentUser } = useAuth();
    const Icon = item.icon;
    const isManualOnly = (item.baseId || item.id) === 'highlight';
    const [pickerOpen, setPickerOpen] = useState(false);
    // Active tab: if Instagram is connected, default to 'instagram', otherwise default to 'manual'
    const [activeTab, setActiveTab] = useState(isInstagramConnected && !isManualOnly ? 'instagram' : 'manual');
    const [manualUrl, setManualUrl] = useState('');
    const [manualUrlError, setManualUrlError] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null); // Post object from IG picker
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSelect = (igPost) => {
        setSelectedPost(igPost);
        setPickerOpen(false);
    };

    const handleOpenPicker = () => {
        if (!isInstagramConnected && !isManualOnly) {
            onInstagramConnect?.();
            return;
        }
        setPickerOpen(prev => !prev);
    };

    const handleConfirmSubmit = async () => {
        if (!selectedPost && !manualUrl.trim()) return;
        if (!selectedPost && !isValidInstagramUrl(manualUrl)) {
            setManualUrlError(true);
            return;
        }
        
        setIsSubmitting(true);
        try {
            const postType = item.baseId || item.id;
            let postUrl = selectedPost?.permalink || '';
            if (!postUrl && postType === 'story' && selectedPost?.id) {
                const igUser = currentUser?.ig_account || currentUser?.username || 'creator';
                postUrl = `https://www.instagram.com/stories/${igUser}/${selectedPost.id}/`;
            }

            const payload = selectedPost ? {
                post_url: postUrl,
                post_type: postType,
                post_code: selectedPost.id,
                media_id: selectedPost.id,
                like_count: selectedPost.like_count,
                comments_count: selectedPost.comments_count,
                media_type: selectedPost.media_type,
                thumbnail_url: selectedPost.thumbnail_url || '',
                media_url: selectedPost.media_url || '',
            } : {
                post_url: manualUrl.trim(),
                post_type: postType,
                post_code: `MANUAL-${Date.now()}`,
            };

            const response = await submitCollabPost(offerId, payload);
            const data = response.data;
            toast.success(t('submission.messages.success', {
                type: t(`picker.types.${item.baseId || item.id}`),
            }));
            announcePwaInstallOpportunity('creator_submitted_deliverable');
            onSubmitted(item.baseId || item.id, {
                id: data.id,
                post_type: data.post_type || payload.post_type,
                instagram_media_id: data.instagram_media_id || payload.media_id || payload.post_code,
                post_url: payload.post_url,
                thumbnail_url: selectedPost?.thumbnail_url || selectedPost?.media_url || null,
                media_url: selectedPost?.media_url || null,
                insights: data.insights || {},
                insights_refresh_state: data.metrics_status || 'pending',
            }, data.deliverables);
            setSelectedPost(null);
            setManualUrl('');
            setManualUrlError(false);
        } catch (err) {
            console.error('Submission error:', err);
            toast.error(err.response?.data?.error || t('submission.messages.network_error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles['deliverable-card']}>
            {!commandCenter && (
                <div className={styles['card-header']}>
                     <div className={styles['type-info']}>
                         <div className={styles['icon-box']}><Icon size={22} /></div>
                         <div className={styles['labels']}>
                             <h4>{t(item.title)} {item.total > 1 ? `(${item.index}/${item.total})` : ''}</h4>
                             <p>{t(item.helper)}</p>
                             <span className={`${styles['deliverable-status']} ${styles.pending}`}>
                                 {t('submission.status.pending', 'Pending')}
                             </span>
                         </div>
                     </div>
                     {/* Badges grouped for mobile-friendly layout */}
                     <div className={styles['header-badges']}>
                         {item.isOpenAdd && (
                             <Badge variant="outline" className={styles['badge-soft-gold']}>
                                 {t('submission.area.add_content')}
                             </Badge>
                         )}
                         {!isInstagramConnected && !isManualOnly && (
                             <Badge variant="outline" className={styles['badge-soft-warning']}>
                                 {t('submission.area.tab_manual', 'Enlace directo')}
                             </Badge>
                         )}
                     </div>
                </div>
            )}

            {/* Mode selector tabs (Only if not manual-only) */}
            {!isManualOnly && (
                <div className={styles['submission-tab-selector']} role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'instagram'}
                        className={`${styles['tab-btn']} ${activeTab === 'instagram' ? styles['is-active'] : ''}`}
                        onClick={() => {
                            setActiveTab('instagram');
                            setManualUrl('');
                        }}
                    >
                        <InstagramGlyph />
                        <span>{t('submission.area.tab_instagram', 'Desde Instagram')}</span>
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'manual'}
                        className={`${styles['tab-btn']} ${activeTab === 'manual' ? styles['is-active'] : ''}`}
                        onClick={() => {
                            setActiveTab('manual');
                            setPickerOpen(false);
                            setSelectedPost(null);
                        }}
                    >
                        <Link2 size={14} />
                        <span>{t('submission.area.tab_manual', 'Enlace directo')}</span>
                    </button>
                </div>
            )}

            <div className={styles['submission-area']}>
                {activeTab === 'instagram' && !isManualOnly ? (
                    !isInstagramConnected ? (
                        <div className={styles['ig-connect-card-inline']}>
                            <div className={styles['ig-connect-header']}>
                                <div className={styles['ig-connect-icon']}><Sparkles size={18} /></div>
                                <div className={styles['ig-connect-text']}>
                                    <strong>{t('submission.warnings.need_connect', 'Conecta tu cuenta de Instagram')}</strong>
                                    <p>{t('submission.warnings.need_connect_help', 'Para seleccionar tus publicaciones con un solo clic y sincronizar métricas automáticamente.')}</p>
                                </div>
                            </div>
                            <div className={styles['ig-connect-actions']}>
                                <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={onInstagramConnect}
                                >
                                    <InstagramGlyph /> {t('submission.warnings.connect_btn', 'Conectar Instagram')}
                                </Button>
                                <button 
                                    type="button" 
                                    className={styles['switch-manual-link']} 
                                    onClick={() => setActiveTab('manual')}
                                >
                                    {t('submission.area.not_found', '¿Prefieres registrar con enlace directo?')}
                                </button>
                            </div>
                        </div>
                    ) : !selectedPost ? (
                        <div className={styles['instagram-select-wrapper']}>
                            <Button
                                variant="primary"
                                className={styles['instagram-select-btn-v2']}
                                onClick={handleOpenPicker}
                            >
                                <InstagramGlyph />
                                <span>{pickerOpen ? t('submission.area.cancel_selection', 'Cancelar selección') : t('submission.area.select_ig', 'Seleccionar desde Instagram')}</span>
                                {pickerOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </Button>
                            <p className={styles['select-hint']}>
                                {t('submission.titles.attach_instagram_content_help', 'Selecciona el post o reel de tu cuenta para vincular métricas automáticamente.')}
                            </p>
                        </div>
                    ) : (
                        <div className={styles['selected-preview-box']}>
                            <div className={styles['preview-card']}>
                                <div className={styles['img-wrapper']}>
                                    <img src={selectedPost.thumbnail_url || selectedPost.media_url} alt="IG Post" />
                                </div>
                                <div className={styles['meta-info']}>
                                    <p className={styles['caption']}>
                                        {selectedPost.caption?.slice(0, 100) || t('submission.area.no_caption')}
                                        {selectedPost.caption?.length > 100 && '...'}
                                    </p>
                                    <div className={styles['stats-strip']}>
                                        <span><Heart size={12} /> {selectedPost.like_count || 0}</span>
                                        <span><MessageCircle size={12} /> {selectedPost.comments_count || 0}</span>
                                        <span><Calendar size={12} /> {formatDate(selectedPost.timestamp, i18n.language)}</span>
                                    </div>
                                </div>
                                <button className={styles['remove-selection']} onClick={() => setSelectedPost(null)}>
                                    &times;
                                </button>
                            </div>

                            <Button
                                variant="primary"
                                fullWidth
                                className={styles['confirm-selected-btn']}
                                onClick={handleConfirmSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <><Loader2 className={styles.spin} size={18} /> {t('submission.area.registering_delivery')}</>
                                ) : (
                                    <><CheckCircle2 size={18} /> {t('submission.area.confirm_delivery')}</>
                                )}
                            </Button>
                        </div>
                    )
                ) : (
                    /* Manual Link Submission Tab */
                    <div className={styles['manual-delivery-box-v2']}>
                        {isManualOnly ? (
                            <div className={styles['invited-helper-note']}>
                                <Sparkles size={14} />
                                <span>{t('picker.highlight_fallback_help', 'Instagram no permite seleccionar historias destacadas mediante su API. Pega el enlace directo a tu destacada.')}</span>
                            </div>
                        ) : !isInstagramConnected && (
                            <div className={styles['invited-helper-note']}>
                                <Sparkles size={14} />
                                <span>{t('submission.area.invited_quick_submit', 'Como creador invitado, puedes registrar tu entrega pegando el enlace directo de tu publicación.')}</span>
                            </div>
                        )}
                        <label className={styles['manual-input-label']}>
                            {t('submission.area.placeholder', 'Pega el enlace de tu post aquí (ej: instagram.com/p/...)')}
                        </label>
                        <div className={styles['manual-input-wrap']}>
                            <input 
                                type="url"
                                placeholder="https://www.instagram.com/p/..."
                                className={styles['manual-url-input']}
                                value={manualUrl}
                                onChange={(e) => {
                                    setManualUrl(e.target.value);
                                    if (manualUrlError) setManualUrlError(false);
                                }}
                                onBlur={() => {
                                    if (manualUrl.trim()) setManualUrlError(!isValidInstagramUrl(manualUrl));
                                }}
                                aria-invalid={manualUrlError}
                                disabled={isSubmitting}
                            />
                        </div>
                        {manualUrlError && (
                            <p className={styles['field-error']} role="alert">
                                {t('submission.area.invalid_url', 'Pega un enlace válido de una publicación de Instagram.')}
                            </p>
                        )}
                        <p className={styles['manual-note']}>
                            {t('submission.area.manual_note', 'Nota: Las métricas automáticas podrían tardar en aparecer para entregas manuales.')}
                        </p>
                        <Button
                            variant="primary"
                            fullWidth
                            onClick={handleConfirmSubmit}
                            disabled={isSubmitting || !manualUrl.trim() || manualUrlError}
                            loading={isSubmitting}
                        >
                            {isSubmitting ? t('submission.area.registering', 'Registrando...') : t('submission.area.confirm_manual', 'Confirmar entrega por enlace')}
                        </Button>
                    </div>
                )}

                {/* The Instagram picker follows the journey guide and opens as a modal. */}
                {pickerOpen && activeTab === 'instagram' && isInstagramConnected && (
                    <InstagramContentPicker
                        isOpen={pickerOpen}
                        onClose={() => setPickerOpen(false)}
                        onSelect={handleSelect}
                        onConnect={onInstagramConnect}
                        onManualFallback={() => {
                            setPickerOpen(false);
                            setActiveTab('manual');
                        }}
                        postType={item.baseId || item.id}
                        offerId={offerId}
                    />
                )}
            </div>
        </div>
    );
};

// --- Main Component ---
const CreatorSubmissionPanel = ({ 
    offerId, 
    isWinner, 
    isInstagramConnected = false, 
    onInstagramConnect,
    requiredDeliverables = [], 
    deliverablesCounts = {}, 
    deliverablesMode = 'defined',
    onDeliverablesProgress,
    onPerformanceUpdate,
    commandCenter = false,
}) => {
    const { t } = useTranslation('creator');
    const { currentUser } = useAuth();
    // submitted: { post_type -> [{ id, instagram_media_id, post_url, thumbnail_url, media_url, insights }] }
    const [submitted, setSubmitted] = useState({});
    const [refreshingType, setRefreshingType] = useState(null);
    const [withdrawingPost, setWithdrawingPost] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [serverDeliverables, setServerDeliverables] = useState(null);

    // Filter deliverables based on what the offer requires
    const expandedDeliverables = useMemo(() => {
        if (deliverablesMode === 'open') {
            const openDeliverables = requiredDeliverables?.length ? requiredDeliverables : ['post', 'story', 'reel'];
            const mapping = {
                '1': 'post',
                '2': 'story',
                '3': 'reel',
                'post': 'post',
                'story': 'story',
                'reel': 'reel',
                'highlight': 'highlight',
                'highlights': 'highlight'
            };

            return openDeliverables
                .map(req => {
                    const baseId = mapping[String(req).toLowerCase()] || String(req).toLowerCase();
                    const originalItem = DELIVERABLES.find(d => d.id === baseId);
                    if (!originalItem) return null;
                    return {
                        ...originalItem,
                        instanceId: `${baseId}-open`,
                        index: 1,
                        total: 1,
                        baseId
                    };
                })
                .filter(Boolean);
        }

        if (!requiredDeliverables || requiredDeliverables.length === 0) return DELIVERABLES;
        
        const mapping = {
            '1': 'post',
            '2': 'story',
            '3': 'reel',
            'post': 'post',
            'story': 'story',
            'reel': 'reel',
            'highlight': 'highlight',
            'highlights': 'highlight'
        };

        const result = [];
        requiredDeliverables.forEach(req => {
            const baseId = mapping[String(req).toLowerCase()] || String(req).toLowerCase();
            const originalItem = DELIVERABLES.find(d => d.id === baseId);
            
            if (originalItem) {
                let count = deliverablesCounts[req] || deliverablesCounts[baseId] || 1;
                count = parseInt(count, 10);
                if (isNaN(count) || count < 1) count = 1;

                for (let i = 1; i <= count; i++) {
                    result.push({
                        ...originalItem,
                        instanceId: `${baseId}-${i}`,
                        index: i,
                        total: count,
                        baseId: baseId
                    });
                }
            }
        });
        return result;
    }, [requiredDeliverables, deliverablesCounts, deliverablesMode]);

    const isOpenMode = deliverablesMode === 'open';
    const submittedCount = Object.values(submitted).reduce((sum, rows) => sum + (Array.isArray(rows) ? rows.length : 0), 0);
    const definedTotalDeliverables = serverDeliverables?.required_total ?? expandedDeliverables.length;
    const totalDeliverables = isOpenMode ? null : Math.max(definedTotalDeliverables, submittedCount);
    const pendingCount = totalDeliverables == null ? 0 : Math.max(totalDeliverables - submittedCount, 0);

    // Communicate deliverables progress to parent
    useEffect(() => {
        if (initialLoading) return;
        const isCompleted = isOpenMode
            ? false
            : (serverDeliverables?.is_complete ?? (totalDeliverables > 0 && submittedCount >= totalDeliverables));

        onDeliverablesProgress?.({
            submittedCount,
            totalDeliverables,
            isCompleted,
            isOpenMode,
            hasSubmissions: serverDeliverables?.has_submissions ?? submittedCount > 0,
            isMinimumSatisfied: isOpenMode ? submittedCount > 0 : isCompleted,
            items: Object.entries(submitted).flatMap(([baseId, posts]) => {
                const definition = DELIVERABLES.find(item => item.id === baseId);
                const configuredTotal = expandedDeliverables.filter(item => item.baseId === baseId).length
                    || parseInt(deliverablesCounts?.[baseId], 10)
                    || 1;
                const itemTotal = isOpenMode ? posts.length : Math.max(configuredTotal, posts.length);
                return (posts || []).map((post, index) => ({
                    id: post.id || post.instagram_media_id || `${baseId}-${index + 1}`,
                    baseId,
                    label: definition ? t(definition.title) : baseId,
                    index: index + 1,
                    total: itemTotal,
                }));
            }),
        });
    }, [submitted, submittedCount, totalDeliverables, isOpenMode, initialLoading, onDeliverablesProgress, onPerformanceUpdate, serverDeliverables, deliverablesCounts, expandedDeliverables, t]);

    // On mount: check which post_types have already been submitted
    const checkExistingSubmissions = useCallback(async () => {
        if (!offerId) return;
        try {
            const response = await getCollabMetrics(offerId);
            const data = response.data;
            setServerDeliverables(data.deliverables || null);
            const submittedMap = {};
            const posts = [...(data.posts || [])].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
            posts.forEach(post => {
                const ptype = post.post_type || post.insights?.post_type || 'post';
                if (!submittedMap[ptype]) submittedMap[ptype] = [];
                submittedMap[ptype].push({
                    id: post.id,
                    post_type: ptype,
                    instagram_media_id: post.instagram_media_id || post.insights?.post_code,
                    post_url: post.post_url,
                    thumbnail_url: post.thumbnail_url || post.insights?.thumbnail_url || null,
                    media_url: post.media_url || post.insights?.media_url || post.post_url,
                    insights: post.insights || {},
                    business_approved: Boolean(post.business_approved),
                    insights_refresh_state: post.insights_refresh_state,
                    emv: post.emv ?? post.insights?.emv ?? data.performance?.content_metrics?.[post.id]?.total_emv ?? null,
                    created_at: post.created_at,
                });
            });
            setSubmitted(submittedMap);
            onPerformanceUpdate?.(data.performance || null);
        } catch (e) {
            console.error('Error fetching existing submissions:', e);
            onPerformanceUpdate?.(null);
        } finally {
            setInitialLoading(false);
        }
    }, [offerId, onPerformanceUpdate]);

    useEffect(() => { checkExistingSubmissions(); }, [checkExistingSubmissions]);

    const handleSubmitted = (postType, postData, deliverables) => {
        if (deliverables) setServerDeliverables(deliverables);
        setSubmitted(prev => {
            const arr = [...(prev[postType] || [])];
            const existingIndex = arr.findIndex(post => (
                (postData.id && post.id === postData.id)
                || (postData.instagram_media_id && post.instagram_media_id === postData.instagram_media_id)
            ));
            if (existingIndex >= 0) {
                arr[existingIndex] = { ...arr[existingIndex], ...postData };
            } else {
                arr.push(postData);
            }
            return { ...prev, [postType]: arr };
        });
    };

    const handleWithdraw = async (postType, post) => {
        const identity = post?.id || post?.instagram_media_id;
        if (!identity) return;

        setWithdrawingPost(identity);
        try {
            await withdrawCollabPost(offerId, {
                post_id: post.id,
                media_id: post.instagram_media_id,
            });
            setSubmitted(prev => {
                const arr = (prev[postType] || []).filter(item => (
                    item.id !== post.id && item.instagram_media_id !== post.instagram_media_id
                ));
                return { ...prev, [postType]: arr };
            });
            toast.success(t('submission.messages.withdraw_success'));
        } catch (err) {
            console.error('Withdraw error:', err);
            if (err?.response?.data?.code === 'delivery_approved_immutable') {
                toast.info(t('submission.status.remove_locked', 'This delivery was approved and cannot be removed.'));
                await checkExistingSubmissions();
            } else {
                toast.error(t('submission.messages.withdraw_error'));
            }
        } finally {
            setWithdrawingPost(null);
        }
    };

    const handleRefresh = async (postType) => {
        setRefreshingType(postType);
        await checkExistingSubmissions();
        setRefreshingType(null);
        toast.info(t('submission.messages.metrics_updated'));
    };

    const commandItems = useMemo(() => {
        if (isOpenMode) {
            return expandedDeliverables.flatMap(item => {
                const rows = submitted[item.baseId] || [];
                const registered = rows.map((post, index) => ({
                    ...item,
                    instanceId: `${item.baseId}-registered-${post.id || post.instagram_media_id || index}`,
                    index: index + 1,
                    total: rows.length,
                    post,
                }));
                return [...registered, {
                    ...item,
                    instanceId: `${item.baseId}-add-${rows.length + 1}`,
                    index: rows.length + 1,
                    total: null,
                    isOpenAdd: true,
                    post: null,
                }];
            });
        }

        return expandedDeliverables.map(item => ({
            ...item,
            post: (submitted[item.baseId] || [])[item.index - 1] || null,
        }));
    }, [expandedDeliverables, isOpenMode, submitted]);

    const [selectedCommandId, setSelectedCommandId] = useState(null);
    const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
    const queuePanelRef = useRef(null);
    const detailPanelRef = useRef(null);
    const wasMobileDetailOpen = useRef(false);
    useEffect(() => {
        if (!commandItems.length) {
            setSelectedCommandId(null);
            return;
        }
        if (!commandItems.some(item => item.instanceId === selectedCommandId)) {
            const firstPending = commandItems.find(item => !item.post);
            setSelectedCommandId((firstPending || commandItems[0]).instanceId);
        }
    }, [commandItems, selectedCommandId]);

    const selectedCommandItem = commandItems.find(item => item.instanceId === selectedCommandId) || commandItems[0];

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 767px)').matches) return;

        if (mobileDetailOpen) {
            wasMobileDetailOpen.current = true;
            window.requestAnimationFrame(() => detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        } else if (wasMobileDetailOpen.current) {
            wasMobileDetailOpen.current = false;
            window.requestAnimationFrame(() => queuePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        }
    }, [mobileDetailOpen, selectedCommandId]);

    const renderCommandCenter = () => {
        if (!selectedCommandItem) return null;
        const commandTotal = isOpenMode ? null : totalDeliverables || expandedDeliverables.length;
        const commandProgress = commandTotal ? Math.min(100, Math.round((submittedCount / commandTotal) * 100)) : submittedCount > 0 ? 100 : 0;
        const isCommandComplete = commandTotal !== null && submittedCount >= commandTotal;
        const refreshKey = selectedCommandItem.instanceId;
        const selectedPostKey = selectedCommandItem.post?.id || selectedCommandItem.post?.instagram_media_id;

        const selectedIndex = commandItems.findIndex(item => item.instanceId === selectedCommandItem.instanceId);
        const hasPrev = selectedIndex > 0;
        const hasNext = selectedIndex >= 0 && selectedIndex < commandItems.length - 1;
        const goToPrev = () => {
            if (hasPrev) {
                setSelectedCommandId(commandItems[selectedIndex - 1].instanceId);
            }
        };
        const goToNext = () => {
            if (hasNext) {
                setSelectedCommandId(commandItems[selectedIndex + 1].instanceId);
            }
        };

        return (
            <div className={styles['command-panel']}>
                <div className={styles['command-status-band']}>
                    <div className={styles['command-info-icon']}>i</div>
                    <div className={styles['command-status-copy']}>
                        <strong>{t('submission.command.independent_title', 'Each delivery advances independently')}</strong>
                        <span>{t('submission.command.independent_body', 'Register one piece at a time. You do not need to wait for every delivery before FOTH starts collecting its live Instagram signals.')}</span>
                    </div>
                    <div className={styles['command-status-stats']}>
                        <div><span>{t('submission.command.completed', 'Completed')}</span><strong>{commandTotal ? `${submittedCount}/${commandTotal}` : submittedCount}</strong></div>
                        <div><span>{t('submission.command.pending', 'Pending')}</span><strong>{commandTotal == null ? '—' : Math.max(commandTotal - submittedCount, 0)}</strong></div>
                        <div><span>Instagram</span><strong>{isInstagramConnected ? t('submission.command.connected', 'Connected') : t('submission.command.not_connected', 'Not connected')}</strong></div>
                    </div>
                </div>

                <div className={`${styles['command-layout']} ${mobileDetailOpen ? styles['is-mobile-detail-open'] : ''}`}>
                    <section className={styles['command-queue-panel']} ref={queuePanelRef}>
                        <div className={styles['command-panel-head']}>
                            <div>
                                <h2>{t('submission.command.your_deliverables', 'Your deliverables')}</h2>
                                <p>{t('submission.command.queue_help', 'Select a deliverable to attach content, inspect the registered publication, or manage the piece already connected to this collab.')}</p>
                            </div>
                            <span className={`${styles['command-pending-pill']} ${isCommandComplete ? styles['is-complete'] : ''}`}>
                                {commandTotal == null
                                    ? t('submission.titles.open_pending', 'Open')
                                    : isCommandComplete
                                        ? t('submission.titles.completion_count', '{{completed}} of {{total}} complete', { completed: submittedCount, total: commandTotal })
                                        : t('submission.titles.pending_count', '{{count}} pending', { count: Math.max(commandTotal - submittedCount, 0) })}
                            </span>
                        </div>
                        <div className={styles['command-columns']}>
                            <span>{t('submission.command.deliverable', 'Deliverable')}</span>
                            <span>{t('submission.command.content', 'Content')}</span>
                            <span>{t('submission.command.status', 'Status')}</span>
                            <span aria-hidden="true" />
                        </div>
                        <div className={styles['command-queue']}>
                            {commandItems.map((item, index) => {
                                const Icon = item.icon;
                                const registered = Boolean(item.post);
                                const isSelected = item.instanceId === selectedCommandItem.instanceId;
                                const viewsCount = item.post?.insights?.views ?? item.post?.insights?.plays ?? item.post?.insights?.impressions;
                                const engagement = item.post?.insights?.engagement_rate;
                                return (
                                    <button
                                        type="button"
                                        key={item.instanceId}
                                        aria-pressed={isSelected}
                                        className={`${styles['command-queue-row']} ${isSelected ? styles['is-active'] : ''} ${registered ? styles['is-registered'] : ''}`}
                                        onClick={() => {
                                            setSelectedCommandId(item.instanceId);
                                            setMobileDetailOpen(true);
                                        }}
                                    >
                                        <span className={styles['command-identity']}>
                                            <span className={styles['command-index']}>{registered ? <CheckCircle2 size={16} /> : <Icon size={16} />}</span>
                                            <span className={styles['command-info-text']}>
                                                <strong>{t(item.title)} {item.total > 1 ? `(${item.index}/${item.total})` : ''}</strong>
                                                <small>
                                                    {item.isOpenAdd
                                                        ? t('submission.command.add_piece', 'Add a piece')
                                                        : registered
                                                            ? (isUsableMetric(viewsCount)
                                                                ? `${formatMetricValue(viewsCount)} ${t('submission.status.views', 'vistas')}${engagement != null ? ` · ${Number(engagement).toFixed(1)}%` : ''}`
                                                                : t('submission.command.registered_content', 'Registered content'))
                                                            : t('submission.command.ready_to_attach', 'Ready to attach content')}
                                                </small>
                                            </span>
                                        </span>
                                        <span className={styles['command-content-type']}>{t(`picker.types.${item.baseId || item.id}`, item.baseId || item.id)}</span>
                                        <span className={styles['command-status-cell']}>
                                            <span className={`${styles['command-status-text']} ${registered ? styles['is-registered'] : ''}`}>
                                                {registered ? t('submission.status.submitted', 'Registered') : t('submission.status.pending', 'Pending')}
                                            </span>
                                            {isSelected && (
                                                <span className={`${styles['command-active-pill']} ${registered ? styles['is-registered'] : ''}`}>
                                                    {t('submission.command.active_row', 'En pantalla')}
                                                </span>
                                            )}
                                        </span>
                                        <span className={styles['command-row-caret']} aria-hidden="true">
                                            <ChevronRight size={16} />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className={styles['command-queue-foot']}>
                            <div><strong>{commandTotal == null ? t('submission.titles.open_count', '{{count}} deliveries registered', { count: submittedCount }) : t('submission.titles.completion_count', '{{completed}} of {{total}} completed', { completed: submittedCount, total: commandTotal })}</strong><span>{commandTotal == null ? t('submission.command.open_help', 'Add the pieces that best represent this collab.') : isCommandComplete ? t('submission.command.review_ready', 'All required deliverables are registered. Continue to review.') : t('submission.command.review_help', 'Complete every required delivery before moving to review.')}</span></div>
                            <div className={styles['command-progress']}><i style={{ width: `${commandProgress}%` }} /></div>
                        </div>
                    </section>

                    <aside className={`${styles['command-detail-panel']} ${selectedCommandItem.post ? styles['is-registered'] : ''}`} ref={detailPanelRef}>
                        <button type="button" className={styles['command-detail-back']} onClick={() => setMobileDetailOpen(false)}>
                            <ChevronLeft size={17} aria-hidden="true" />
                            {t('submission.command.back_to_queue', 'Back to deliverables')}
                        </button>

                        {commandItems.length > 1 && (
                            <div className={styles['command-mobile-selector-strip']}>
                                <div className={styles['command-mobile-selector-label']}>
                                    <span>{t('submission.command.mobile_selector_label', 'Entregables del collab')}</span>
                                    <strong>{selectedIndex >= 0 ? `${selectedIndex + 1} / ${commandItems.length}` : ''}</strong>
                                </div>
                                <div className={styles['command-mobile-chips']} role="tablist">
                                    {commandItems.map((item) => {
                                        const Icon = item.icon;
                                        const registered = Boolean(item.post);
                                        const isSelected = item.instanceId === selectedCommandItem.instanceId;
                                        return (
                                            <button
                                                key={item.instanceId}
                                                type="button"
                                                role="tab"
                                                aria-selected={isSelected}
                                                className={`${styles['command-mobile-chip']} ${isSelected ? styles['is-active'] : ''} ${registered ? styles['is-registered'] : ''}`}
                                                onClick={() => setSelectedCommandId(item.instanceId)}
                                            >
                                                <span className={styles['chip-icon-box']}>
                                                    {registered ? <CheckCircle2 size={13} /> : <Icon size={13} />}
                                                </span>
                                                <span className={styles['chip-text']}>
                                                    {t(item.title)} {item.total > 1 ? `#${item.index}` : ''}
                                                </span>
                                                <span className={`${styles['chip-status-badge']} ${registered ? styles['is-registered'] : ''}`}>
                                                    {registered ? '✓' : '⚡'}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className={styles['command-detail-head']}>
                            <div>
                                <span className={styles['command-detail-eyebrow']}>
                                    {t('submission.command.selected', 'Selected deliverable')}
                                </span>
                                <h3>
                                    {t(selectedCommandItem.title)} {selectedCommandItem.total > 1 ? `(${selectedCommandItem.index}/${selectedCommandItem.total})` : ''}
                                </h3>
                                <p>
                                    {selectedCommandItem.post
                                        ? t('submission.command.registered_content', 'Registered content')
                                        : t('submission.command.ready_to_attach', 'Ready to attach content')}
                                </p>
                            </div>
                            <span className={`${styles['command-detail-status']} ${selectedCommandItem.post?.business_approved ? styles['is-approved'] : selectedCommandItem.post ? styles['is-registered'] : ''}`}>
                                {selectedCommandItem.post?.business_approved
                                    ? t('submission.status.approved', 'Aprobado')
                                    : selectedCommandItem.post
                                        ? t('submission.status.submitted', 'Registered')
                                        : t('submission.status.pending', 'Pending')}
                            </span>
                        </div>
                        <div className={styles['command-detail-body']}>
                            {selectedCommandItem.post ? (
                                <CommandDeliverableDetail
                                    item={selectedCommandItem}
                                    post={selectedCommandItem.post}
                                    isRefreshing={refreshingType === refreshKey}
                                    isWithdrawing={withdrawingPost === selectedPostKey}
                                    onRefresh={() => handleRefresh(refreshKey)}
                                    onWithdraw={() => handleWithdraw(selectedCommandItem.baseId, selectedCommandItem.post)}
                                    isInstagramConnected={isInstagramConnected}
                                    currentUser={currentUser}
                                />
                            ) : (
                                <PendingCard
                                    item={selectedCommandItem}
                                    offerId={offerId}
                                    isInstagramConnected={isInstagramConnected}
                                    onInstagramConnect={onInstagramConnect}
                                    onSubmitted={handleSubmitted}
                                    commandCenter
                                />
                            )}
                        </div>

                        {commandItems.length > 1 && (
                            <div className={styles['command-nav-stepper']}>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={goToPrev}
                                    disabled={!hasPrev}
                                    className={styles['command-step-btn']}
                                >
                                    <ChevronLeft size={15} />
                                    <span>{t('submission.command.prev_deliverable', 'Anterior')}</span>
                                </Button>
                                <span className={styles['command-step-indicator']}>
                                    {selectedIndex + 1} / {commandItems.length}
                                </span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={goToNext}
                                    disabled={!hasNext}
                                    className={styles['command-step-btn']}
                                >
                                    <span>{t('submission.command.next_deliverable', 'Siguiente')}</span>
                                    <ChevronRight size={15} />
                                </Button>
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        );
    };

    if (initialLoading) {
        return (
            <div className={styles['panel-loading']}>
                <Loader2 className={styles.spin} size={28} />
            </div>
        );
    }

    return (
        commandCenter ? renderCommandCenter() : <div className={styles['submission-panel']}>
             <div className={styles['deliverables-section-header']}>
                  <div className={styles['deliverables-heading-copy']}>
                      <p className={styles.eyebrow}>{t('submission.titles.open_deliverables', 'Open Deliverables')}</p>
                      <h3>{t('submission.titles.attach_instagram_content', 'Attach your Instagram content')}</h3>
                       <p>
                           {t(
                               isOpenMode ? 'submission.titles.open_deliverables_help' : 'submission.titles.attach_instagram_content_help',
                               isOpenMode
                                   ? 'Comparte las piezas que mejor representan tu colaboración. No hay una cantidad fija de entregas.'
                                   : 'Select published pieces from your connected account. FOTH will associate the content with this Collab to review metrics and complete the report.'
                           )}
                       </p>
                   </div>
                   <span className={`${styles['deliverables-count-pill']} ${!isOpenMode && submittedCount >= totalDeliverables && totalDeliverables > 0 ? styles['is-completed'] : ''}`}>
                       {!isOpenMode && submittedCount >= totalDeliverables && totalDeliverables > 0 && <CheckCircle2 size={13} />}
                       {isOpenMode
                           ? submittedCount > 0
                               ? t('submission.titles.open_count', '{{count}} entregas registradas', { count: submittedCount })
                               : t('submission.titles.open_pending', 'Puedes agregar tus entregas')
                           : submittedCount > 0
                               ? t('submission.titles.completion_count', '{{completed}} de {{total}} completados', { completed: submittedCount, total: totalDeliverables })
                               : t('submission.titles.pending_count', '{{count}} pendientes', { count: pendingCount || totalDeliverables })}
                   </span>
              </div>

              {!isOpenMode && totalDeliverables > 0 && (
                 <div className={styles['deliverables-progress-wrapper']}>
                     <div className={styles['deliverables-progress-track']}>
                         <div 
                             className={styles['deliverables-progress-fill']} 
                             style={{ width: `${Math.min(100, Math.round((submittedCount / totalDeliverables) * 100))}%` }}
                         />
                     </div>
                     <span className={styles['deliverables-progress-percent']}>
                         {Math.min(100, Math.round((submittedCount / totalDeliverables) * 100))}%
                     </span>
                 </div>
             )}

            <div className={styles['deliverables-grid']}>
                {deliverablesMode === 'open' ? (
                    expandedDeliverables.map(item => {
                        const typeSubmissions = submitted[item.baseId] || [];
                        const addItem = {
                            ...item,
                            instanceId: `${item.baseId}-add-${typeSubmissions.length + 1}`,
                            index: typeSubmissions.length + 1,
                            total: null,
                            isOpenAdd: true,
                        };

                        return (
                            <React.Fragment key={item.instanceId}>
                                <AnimatePresence initial={false}>
                                    {typeSubmissions.map((sub, idx) => {
                                        const cardKey = sub.id || sub.instagram_media_id || `${item.baseId}-${idx}`;
                                        const cardItem = {
                                            ...item,
                                            index: idx + 1,
                                            total: typeSubmissions.length,
                                        };
                                        const busyKey = sub.id || sub.instagram_media_id;

                                        return (
                                            <motion.div
                                                key={cardKey}
                                                layout
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -12 }}
                                            >
                                                <SubmittedCard
                                                    item={cardItem}
                                                    post={sub}
                                                    isRefreshing={refreshingType === cardKey}
                                                    isWithdrawing={withdrawingPost === busyKey}
                                                    onRefresh={() => handleRefresh(cardKey)}
                                                    onWithdraw={() => handleWithdraw(item.baseId, sub)}
                                                    isInstagramConnected={isInstagramConnected}
                                                />
                                            </motion.div>
                                        );
                                    })}
                                </AnimatePresence>
                                <PendingCard
                                    key={addItem.instanceId}
                                    item={addItem}
                                    offerId={offerId}
                                    isInstagramConnected={isInstagramConnected}
                                    onInstagramConnect={onInstagramConnect}
                                    onSubmitted={handleSubmitted}
                                />
                            </React.Fragment>
                        );
                    })
                ) : (
                    <AnimatePresence initial={false}>
                        {expandedDeliverables.map(item => {
                            const typeSubmissions = submitted[item.baseId] || [];
                            const sub = typeSubmissions[item.index - 1];
                            const busyKey = sub?.id || sub?.instagram_media_id;

                            return (
                                <motion.div
                                    key={item.instanceId}
                                    layout
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -12 }}
                                >
                                    {sub ? (
                                        <SubmittedCard
                                            item={item}
                                            post={sub}
                                            isRefreshing={refreshingType === item.instanceId}
                                            isWithdrawing={withdrawingPost === busyKey}
                                            onRefresh={() => handleRefresh(item.instanceId)}
                                            onWithdraw={() => handleWithdraw(item.baseId, sub)}
                                            isInstagramConnected={isInstagramConnected}
                                        />
                                    ) : (
                                        <PendingCard
                                            item={item}
                                            offerId={offerId}
                                            isInstagramConnected={isInstagramConnected}
                                            onInstagramConnect={onInstagramConnect}
                                            onSubmitted={handleSubmitted}
                                        />
                                    )}
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                )}
            </div>
        </div>
    );
};

export default CreatorSubmissionPanel;
